import { GAME_MODE, createInitialConnect4State } from "./connectFour";
import { buildLeaderboardAfterRound } from "./connect4Series";
import { CPU_GENEROUS_CHANCE } from "./connectFourCpu";

export const MODE_STORAGE_KEY = "connect4SelectedMode";
export const MODE_CPU = "cpu";
export const MODE_MULTIPLAYER = "multiplayer";

export const CPU_SERIES_KEY = "connect4CpuSeries";
export const CPU_GAME_KEY = "connect4CpuGame";
export const CPU_OPPONENT_ID = "__cpu__";

export function getSelectedGameMode() {
  try {
    const v = String(
      sessionStorage.getItem(MODE_STORAGE_KEY) ||
        localStorage.getItem(MODE_STORAGE_KEY) ||
        ""
    ).trim();
    return v === MODE_CPU ? MODE_CPU : MODE_MULTIPLAYER;
  } catch {
    return MODE_MULTIPLAYER;
  }
}

export function setSelectedGameMode(mode) {
  const value = mode === MODE_CPU ? MODE_CPU : MODE_MULTIPLAYER;
  try {
    sessionStorage.setItem(MODE_STORAGE_KEY, value);
    localStorage.setItem(MODE_STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

export function isCpuModeSelected() {
  return getSelectedGameMode() === MODE_CPU;
}

function readJson(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/**
 * sessionStorage is per browser tab, not per game session, so CPU data is
 * stamped with the session + user it belongs to and ignored on mismatch
 * (e.g. a new session opened in the same tab).
 */
function currentOwnerKey() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    const data = JSON.parse(raw) || {};
    const userId = String(data?.userId ?? data?.userid ?? data?.id ?? "").trim();
    return `${String(data?.sessionId ?? "").trim()}|${userId}`;
  } catch {
    return "|";
  }
}

function readOwned(key) {
  const value = readJson(key, null);
  if (!value || value.ownerKey !== currentOwnerKey()) return null;
  return value;
}

function writeOwned(key, value) {
  writeJson(key, { ...value, ownerKey: currentOwnerKey() });
}

export function loadCpuSeries() {
  return readOwned(CPU_SERIES_KEY);
}

export function saveCpuSeries(series) {
  writeOwned(CPU_SERIES_KEY, series);
}

export function loadCpuGame() {
  return readOwned(CPU_GAME_KEY);
}

export function saveCpuGame(state) {
  writeOwned(CPU_GAME_KEY, state);
}

export function clearCpuSession() {
  try {
    sessionStorage.removeItem(CPU_SERIES_KEY);
    sessionStorage.removeItem(CPU_GAME_KEY);
  } catch {
    // ignore
  }
}

export function createCpuGameState(userId, playerName, series = null) {
  const fresh = createInitialConnect4State({
    player1UserId: String(userId ?? ""),
    player2UserId: CPU_OPPONENT_ID,
    mode: GAME_MODE.CPU,
    cpuPlayer: 2,
  });
  // Unique per round so the backend counts each finished game exactly once.
  fresh.roundId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  fresh.cpuGenerous = Math.random() < CPU_GENEROUS_CHANCE;
  if (series) {
    fresh.player1Wins = Number(series.player1Wins) || 0;
    fresh.player2Wins = Number(series.player2Wins) || 0;
  }
  return fresh;
}

export function syncCpuSeriesAfterRound(roundState, playerName) {
  const existing = loadCpuSeries();
  const next = buildLeaderboardAfterRound(existing, roundState, {
    player1Name: String(playerName || existing?.player1Name || "You"),
    player2Name: "Computer",
  });
  next.player1UserId = String(roundState?.player1UserId ?? "");
  next.player2UserId = CPU_OPPONENT_ID;
  saveCpuSeries(next);
  return next;
}

export function startFreshCpuGame(userId, playerName) {
  const series = loadCpuSeries();
  const game = createCpuGameState(userId, playerName, series);
  saveCpuGame(game);
  return game;
}
